import React, { useState } from 'react';
import {
  Menu,
  MenuItem,
  IconButton,
  Avatar,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Snackbar,
  Alert
} from '@mui/material';
import { AccountCircle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config/api';

interface UserData {
  id: number;
  name: string;
  email: string;
  role?: 'SUPER_ADMIN' | 'REVIEWER' | 'APPROVAL' | 'KASIR' | 'KOMISI';
}

const UserMenu = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openChangePassword, setOpenChangePassword] = useState(false);
  const [openEditProfile, setOpenEditProfile] = useState(false);
  const [openLogoutConfirm, setOpenLogoutConfirm] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [profileData, setProfileData] = useState({
    name: '',
    email: ''
  });

  const navigate = useNavigate();
  const [user, setUser] = React.useState<UserData | null>(() => {
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;
  });
  const token = localStorage.getItem('token');

  React.useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name,
        email: user.email
      });
    }
  }, []);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogoutClick = () => {
    handleClose();
    setOpenLogoutConfirm(true);
  };

  const handleLogoutConfirm = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Kata sandi baru tidak cocok' });
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/api/auth/change-password`, 
        {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setMessage({ type: 'success', text: 'Kata sandi berhasil diubah' });
      setOpenChangePassword(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      setMessage({ type: 'error', text: 'Gagal mengubah kata sandi' });
    }
  };

  const handleUpdateProfile = async () => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/auth/update-profile`,
        profileData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      localStorage.setItem('user', JSON.stringify(response.data));
      setMessage({ type: 'success', text: 'Profil berhasil diperbarui' });
      setOpenEditProfile(false);
    } catch (error) {
      setMessage({ type: 'error', text: 'Gagal memperbarui profil' });
    }
  };

  return (
    <div>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body1">
          {user?.name}
        </Typography>
        <IconButton
          size="large"
          onClick={handleMenu}
          color="inherit"
        >
          <Avatar sx={{ width: 32, height: 32 }}>
            <AccountCircle />
          </Avatar>
        </IconButton>
      </Box>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={() => {
          handleClose();
          setOpenEditProfile(true);
        }}>Edit Profil</MenuItem>
        <MenuItem onClick={() => {
          handleClose();
          setOpenChangePassword(true);
        }}>Ubah Kata Sandi</MenuItem>
        <MenuItem onClick={handleLogoutClick}>Keluar</MenuItem>
      </Menu>

      {/* Change Password Dialog */}
      <Dialog open={openChangePassword} onClose={() => setOpenChangePassword(false)}>
        <DialogTitle>Ubah Kata Sandi</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Kata Sandi Saat Ini"
            type="password"
            fullWidth
            value={passwordData.currentPassword}
            onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Kata Sandi Baru"
            type="password"
            fullWidth
            value={passwordData.newPassword}
            onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Konfirmasi Kata Sandi Baru"
            type="password"
            fullWidth
            value={passwordData.confirmPassword}
            onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenChangePassword(false)}>Batal</Button>
          <Button onClick={handleChangePassword}>Ubah Kata Sandi</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Profile Dialog */}
      <Dialog open={openEditProfile} onClose={() => setOpenEditProfile(false)}>
        <DialogTitle>Edit Profil</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Nama"
            fullWidth
            value={profileData.name}
            onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Email"
            fullWidth
            value={profileData.email}
            disabled
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditProfile(false)}>Batal</Button>
          <Button onClick={handleUpdateProfile}>Perbarui Profil</Button>
        </DialogActions>
      </Dialog>

      {/* Logout Confirmation Dialog */}
      <Dialog open={openLogoutConfirm} onClose={() => setOpenLogoutConfirm(false)}>
        <DialogTitle>Konfirmasi Keluar</DialogTitle>
        <DialogContent>
          <Typography>
            Apakah Anda yakin ingin keluar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLogoutConfirm(false)}>Batal</Button>
          <Button onClick={handleLogoutConfirm} color="error" variant="contained">
            Keluar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for messages */}
      <Snackbar
        open={!!message}
        autoHideDuration={3000}
        onClose={() => setMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={message?.type} sx={{ width: '100%' }}>
          {message?.text}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default UserMenu;
